/**
 * PATH: apps/hr/src/pages/documents/document-detail/DocumentPreview.tsx
 * PURPOSE: Document preview with image, PDF, and fallback rendering + lazy loading
 */

import { useState, useEffect } from 'react'
import { FileText, AlertTriangle, Eye, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'

interface DocumentPreviewProps {
  fileUrl?: string
  fileName?: string
  fileType?: string
}

function LoadingSkeleton() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="relative">
        <div className="w-20 h-24 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
        </div>
      </div>
      <div className="mt-4 space-y-2 text-center">
        <div className="h-3 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-2 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mx-auto" />
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">Loading document...</p>
    </div>
  )
}

function ErrorState({ fileName, fileUrl }: { fileName?: string; fileUrl?: string }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <AlertTriangle className="w-8 h-8 text-red-500" />
      </div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Failed to load document</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{fileName || 'The document could not be loaded'}</p>
      {fileUrl && (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
          <Eye className="w-4 h-4" />Open in New Tab
        </a>
      )}
    </div>
  )
}

export function DocumentPreview({ fileUrl, fileName, fileType }: DocumentPreviewProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isImageVisible, setIsImageVisible] = useState(false)

  const isImage = fileType?.startsWith('image/') || fileUrl?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)/)
  const isPdf = fileType === 'application/pdf' || fileUrl?.toLowerCase().endsWith('.pdf')

  useEffect(() => { setIsLoading(true); setHasError(false); setIsImageVisible(false) }, [fileUrl])

  if (!fileUrl) {
    return (
      <div className="text-center py-8">
        <FileText className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{fileName || 'No document'}</p>
        <p className="text-xs text-slate-400">Document not available</p>
      </div>
    )
  }

  if (isImage) {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        {isLoading && <LoadingSkeleton />}
        {hasError && <ErrorState fileName={fileName} fileUrl={fileUrl} />}
        <img src={fileUrl} alt={fileName || 'Document preview'} loading="lazy"
          className={cn('max-w-full max-h-full object-contain transition-opacity duration-300', isImageVisible ? 'opacity-100' : 'opacity-0')}
          onLoad={() => { setIsLoading(false); setIsImageVisible(true) }}
          onError={() => { setIsLoading(false); setHasError(true) }}
        />
      </div>
    )
  }

  if (isPdf) {
    return (
      <div className="relative w-full h-full">
        {isLoading && <LoadingSkeleton />}
        <iframe src={fileUrl} title={fileName || 'Document preview'} loading="lazy"
          className={cn('w-full h-full border-0 transition-opacity duration-300', isLoading ? 'opacity-0' : 'opacity-100')}
          onLoad={() => setIsLoading(false)}
          onError={() => { setIsLoading(false); setHasError(true) }}
        />
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
            <ErrorState fileName={fileName} fileUrl={fileUrl} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="text-center py-8">
      <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
        <FileText className="w-10 h-10 text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{fileName || 'Document'}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{fileType || 'Unknown file type'}</p>
      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
        <Eye className="w-4 h-4" />View Document
      </a>
    </div>
  )
}
